import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MessageBar, MessageBarType, Text, DialogType } from 'office-ui-fabric-react';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { Dialog, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import DimensionListForm from '../../components/DimensionListForm';
import Module from '../../components/Module';
import Main from '../../components/Main';
import SavingIndicator from '../../components/SavingIndicator';
import { showFlashMessage } from '../../actions/ui';
import { discardDimensionListEdits } from '../../actions/dimensionList';
import ListDefinitionRejectionDialog from '../../components/ListDefinitionRejectionDialog';
import PauseTransactionsDialog from '../../components/PauseTransactionsDialog';
import styles from './AddDimensionListOverview.style';

const AddDimensionListOverview = React.forwardRef(({ dimensionList }, ref) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessageText, setErrorMessageText] = useState('');
  const [listName, setListName] = useState('');
  const [isError, setIsError] = useState(false);
  const [href, setHref] = useState('');
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [isDuplicateName, setIsDuplicateName] = useState(true);
  const [isEditDialogVisible, setIsEditDialogVisible] = useState(false);
  const [isDiscardDialogVisible, setIsDiscardDialogVisible] = useState(false);
  const history = useHistory();
  const { contracts, listId, status } = dimensionList;

  const pauseDialogTitle =
    status === 'Rejected' ? 'Re-submit list revisions for review?' : 'Edits may cause reprocessing';

  const handleCancel = () => {
    history.push('/dimensionLists');
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    const formResponse = await ref.current.submitForm();

    if (formResponse) {
      if (ref.current && formResponse.error) {
        setIsError(true);
        setIsDialogVisible(true);
        setListName(formResponse.payload.body.listName);
        if (formResponse.payload.body.duplicateList) {
          let errorMessage = `Cannot save the list as it matches an existing list name ${formResponse.payload.body.listName}. Please update the list name.`;
          if (
            formResponse.payload.body.message &&
            formResponse.payload.body.message ===
              'List validation error: A list already exists with the same items.'
          ) {
            setIsDuplicateName(false);
            errorMessage = `Cannot save the list as it matches an existing list definition ${formResponse.payload.body.listName}. Please update the list definition.`;
          }
          setErrorMessageText(errorMessage);
          setHref(
            `/dimensionLists/${formResponse.payload.body.listId}/${formResponse.payload.body.listType}`,
          );
        } else {
          setErrorMessageText(formResponse.payload.body.message);
        }
      } else {
        if (listId) history.push(`/dimensionLists/${listId}/${formResponse.listType}`);
        else history.push(`/dimensionLists/${formResponse.listId}/${formResponse.listType}`);

        if (contracts && contracts.length > 0) {
          dispatch(showFlashMessage('Edit submitted for approval', { type: 'success' }));
        } else {
          const flashMessage = listId ? 'List Successfully Revised' : 'List Successfully Created';
          dispatch(showFlashMessage(flashMessage, { type: 'success' }));
        }
      }
    }
    setIsSubmitting(false);
  };

  const handleSubmit = () => {
    if (contracts) {
      if (contracts.length > 0) {
        setIsEditDialogVisible(true);
      } else handleSave();
    } else {
      handleSave();
    }
  };

  const onDiscardEdits = async () => {
    setIsSubmitting(true);
    const response = await dispatch(discardDimensionListEdits(listId));
    if (response.error)
      dispatch(showFlashMessage(response.payload.body.message, { type: 'error' }));
    else {
      history.push(`/dimensionLists/${dimensionList.listId}/${dimensionList.listType}`);
      dispatch(showFlashMessage('Edits successfully discarded', { type: 'success' }));
    }
    setIsSubmitting(false);
  };

  const handleDiscard = () => {
    setIsDiscardDialogVisible(true);
  };

  const handleFailure = () => {
    setIsSubmitting(false);
  };

  const handleSuccess = async () => {
    setIsSubmitting(false);
  };

  const handleDismiss = () => {
    setIsDialogVisible(false);
  };

  return (
    <>
      {isError && (
        <>
          <MessageBar
            messageBarType={MessageBarType.error}
            messageBarIconProps={{ iconName: 'Error' }}
            dismissButtonAriaLabel="Close"
            nowrap
          >
            <Text>Action Required: </Text> {errorMessageText}
          </MessageBar>
          {isDialogVisible && (
            <ListDefinitionRejectionDialog
              onDismiss={() => handleDismiss()}
              onSubmit={() => handleDismiss()}
              hidden={!isError}
              text={listName}
              href={href}
              isDuplicateName={isDuplicateName}
              dialogContentProps={{
                type: DialogType.close,
                title: 'List cannot be saved',
                closeButtonAriaLabel: 'Close',
              }}
            />
          )}
        </>
      )}
      <Main>
        <Module>
          <DimensionListForm
            onSuccess={handleSuccess}
            onFailure={handleFailure}
            ref={ref}
            dimensionList={dimensionList}
            handleCancel={handleCancel}
            handleSubmit={handleSubmit}
            handleDiscard={handleDiscard}
          />
        </Module>

        <Dialog
          dialogContentProps={{
            type: DialogType.normal,
            title: 'Discard List Edits',
            closeButtonAriaLabel: 'Close',
          }}
          onDismiss={() => setIsDiscardDialogVisible(false)}
          onSubmit={onDiscardEdits}
          isSubmitting={isSubmitting}
          hidden={!isDiscardDialogVisible}
        >
          <p className={styles.modalText}>
            Once discarded, paused transactions will be ingested for associated contracts. The list
            will revert back to its previously approved definition.
          </p>

          <DialogFooter>
            <DefaultButton onClick={() => setIsDiscardDialogVisible(false)} disabled={isSubmitting}>
              Cancel
            </DefaultButton>
            <PrimaryButton type="submit" onClick={onDiscardEdits} disabled={isSubmitting}>
              Agree
            </PrimaryButton>
            {isSubmitting && <SavingIndicator className={styles.saving} />}
          </DialogFooter>
        </Dialog>

        <PauseTransactionsDialog
          onDismiss={() => setIsEditDialogVisible(false)}
          onSubmit={handleSave}
          isSubmitting={isSubmitting}
          hidden={!isEditDialogVisible}
          dialogContentProps={{
            type: DialogType.normal,
            title: pauseDialogTitle,
            closeButtonAriaLabel: 'Close',
          }}
          isDimensionList
          isResubmit={status === 'Rejected'}
        />
      </Main>
    </>
  );
});

AddDimensionListOverview.propTypes = {
  dimensionList: PropTypes.object,
};

AddDimensionListOverview.defaultProps = {
  dimensionList: {},
};

export default AddDimensionListOverview;
